import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Save, Link as LinkIcon, Image as ImageIcon, FileText, Youtube, Download, Upload, X, Loader, Sparkles, Brain, Search, Box, Layout, ChevronLeft, ChevronRight, Package, Plus, Trash2, Edit2, ChevronDown, ChevronUp } from 'lucide-react';
import { useI18n } from '../i18n';
import { formatMemberText } from '../locales/member';

const NO_IMAGE_URL = '/empty-game.jpg';

const GameForm = ({ game, onSave, onCancel }) => {
  const { t } = useI18n();
  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm({
    defaultValues: { name: '', bggId: '', imageUrl: '', setupImageUrl: '', rulesUrl: '', manualUrl: '', videoUrl: '' }
  });

  // Image handling state - separate for box and setup images
  const [activeImageTab, setActiveImageTab] = useState('box'); // 'box' or 'setup'
  const [imageSource, setImageSource] = useState('url'); // 'url', 'upload'
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewBoxImage, setPreviewBoxImage] = useState('');
  const [previewSetupImage, setPreviewSetupImage] = useState('');
  
  // Carousel state for preview
  const [carouselIndex, setCarouselIndex] = useState(0);
  
  // AI search state
  const [isAiSearching, setIsAiSearching] = useState(false);
  const [aiFound, setAiFound] = useState(false);
  const [aiGameInfo, setAiGameInfo] = useState(null);
  const aiSearchTimeoutRef = useRef(null);
  
  // Expansions state
  const [expansions, setExpansions] = useState([]);
  const [showExpansions, setShowExpansions] = useState(false);
  const [newExpansion, setNewExpansion] = useState({ name: '', bggId: '', isOwned: true, notes: '' });
  const [editingExpansion, setEditingExpansion] = useState(null);
  const [loadingExpansions, setLoadingExpansions] = useState(false);
  const [availableExpansions, setAvailableExpansions] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [expansionSource, setExpansionSource] = useState(null);
  
  const watchedImageUrl = watch('imageUrl');
  const watchedSetupImageUrl = watch('setupImageUrl');
  const watchedName = watch('name');

  // Update previews when URLs change
  useEffect(() => {
    setPreviewBoxImage(watchedImageUrl || '');
  }, [watchedImageUrl]);

  useEffect(() => {
    setPreviewSetupImage(watchedSetupImageUrl || '');
  }, [watchedSetupImageUrl]);

  // Load expansions when game changes
  const loadExpansions = useCallback(async () => {
    if (!game?.id) {
      setExpansions([]);
      return;
    }
    
    setLoadingExpansions(true);
    try {
      const res = await fetch(`/api/games/${game.id}/expansions`);
      if (res.ok) {
        const data = await res.json();
        setExpansions(data);
      }
    } catch (err) {
      console.error('Failed to load expansions:', err);
    } finally {
      setLoadingExpansions(false);
    }
  }, [game?.id]);

  useEffect(() => {
    loadExpansions();
  }, [loadExpansions]);

  // Load available expansions from BGG/AI
  const loadAvailableExpansions = useCallback(async () => {
    if (!game?.id) return;
    
    setLoadingAvailable(true);
    try {
      const res = await fetch(`/api/games/${game.id}/available-expansions`);
      if (res.ok) {
        const data = await res.json();
        setAvailableExpansions(data.available || []);
        setExpansionSource(data.source);
      }
    } catch (err) {
      console.error('Failed to load available expansions:', err);
    } finally {
      setLoadingAvailable(false);
    }
  }, [game?.id]);

  // Load available expansions when expansions section is opened
  useEffect(() => {
    if (showExpansions && availableExpansions.length === 0 && !loadingAvailable) {
      loadAvailableExpansions();
    }
  }, [showExpansions, availableExpansions.length, loadingAvailable, loadAvailableExpansions]);

  // Add new expansion
  const handleAddExpansion = async () => {
    if (!newExpansion.name.trim() || !game?.id) return;
    
    try {
      const res = await fetch(`/api/games/${game.id}/expansions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newExpansion)
      });
      
      if (res.ok) {
        setNewExpansion({ name: '', bggId: '', isOwned: true, notes: '' });
        loadExpansions();
      }
    } catch (err) {
      console.error('Failed to add expansion:', err);
    }
  };

  // Update expansion
  const handleUpdateExpansion = async (expansion) => {
    try {
      const res = await fetch(`/api/expansions/${expansion.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(expansion)
      });
      
      if (res.ok) {
        setEditingExpansion(null);
        loadExpansions();
      }
    } catch (err) {
      console.error('Failed to update expansion:', err);
    }
  };

  // Delete expansion
  const handleDeleteExpansion = async (expansionId) => {
    if (!window.confirm(t.gameForm.confirmDeleteExpansion)) return;
    
    try {
      const res = await fetch(`/api/expansions/${expansionId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        loadExpansions();
      }
    } catch (err) {
      console.error('Failed to delete expansion:', err);
    }
  };

  // Add expansion from available list (ghost -> owned)
  const handleClaimExpansion = async (expansion) => {
    if (!game?.id) return;
    
    try {
      const res = await fetch(`/api/games/${game.id}/expansions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: expansion.name,
          bggId: expansion.bggId,
          isOwned: true
        })
      });
      
      if (res.ok) {
        // Remove from available and reload owned
        setAvailableExpansions(prev => prev.filter(e => e.name !== expansion.name));
        loadExpansions();
      }
    } catch (err) {
      console.error('Failed to claim expansion:', err);
    }
  };

  // Get all images for carousel
  const allImages = [
    previewBoxImage && { url: previewBoxImage, type: 'box', label: t.gameForm.boxImage },
    previewSetupImage && { url: previewSetupImage, type: 'setup', label: t.gameForm.setupImage }
  ].filter(Boolean);

  // Reset form when game prop changes
  useEffect(() => {
    if (game) {
      reset({
        id: game.id,
        name: game.name || '',
        bggId: game.bgg_id || game.bggId || '',
        imageUrl: game.image_url || game.imageUrl || '',
        setupImageUrl: game.setup_image_url || game.setupImageUrl || '',
        rulesUrl: game.rules_url || game.rulesUrl || '',
        manualUrl: game.manual_url || game.manualUrl || '',
        videoUrl: game.video_url || game.videoUrl || ''
      });
      setPreviewBoxImage(game.image_url || game.imageUrl || '');
      setPreviewSetupImage(game.setup_image_url || game.setupImageUrl || '');
      setAiFound(false);
      setAiGameInfo(null);
    } else {
      reset({ name: '', bggId: '', imageUrl: '', setupImageUrl: '', rulesUrl: '', manualUrl: '', videoUrl: '' });
      setPreviewBoxImage('');
      setPreviewSetupImage('');
      setAiFound(false);
      setAiGameInfo(null);
    }
  }, [game, reset]);

  // Debounced AI search when name changes (only for new games)
  useEffect(() => {
    if (game) return;
    
    if (aiSearchTimeoutRef.current) {
      clearTimeout(aiSearchTimeoutRef.current);
    }
    
    if (!watchedName || watchedName.length < 3) {
      setIsAiSearching(false);
      return;
    }
    
    aiSearchTimeoutRef.current = setTimeout(async () => {
      setIsAiSearching(true);
      setAiFound(false);
      
      try {
        const res = await fetch('/api/ai/search-game', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: watchedName })
        });
        
        const data = await res.json();
        
        if (data.found !== false) {
          if (data.bggId) {
            setValue('bggId', String(data.bggId));
          }
          if (data.imageUrl) {
            setValue('imageUrl', data.imageUrl);
            setPreviewBoxImage(data.imageUrl);
          }
          if (data.rulesUrl) {
            setValue('manualUrl', data.rulesUrl);
          }
          if (data.bggId) {
            setValue('rulesUrl', `https://boardgamegeek.com/boardgame/${data.bggId}`);
          }
          setAiGameInfo({
            description: data.description,
            playerCount: data.minPlayers && data.maxPlayers ? `${data.minPlayers}-${data.maxPlayers}` : null,
            playTime: data.playTime,
            year: data.year,
            designer: null
          });
          setAiFound(true);
          setTimeout(() => setAiFound(false), 5000);
        }
      } catch (err) {
        console.error('AI search error:', err);
      } finally {
        setIsAiSearching(false);
      }
    }, 1000);
    
    return () => {
      if (aiSearchTimeoutRef.current) {
        clearTimeout(aiSearchTimeoutRef.current);
      }
    };
  }, [watchedName, game, setValue]);

  // Handle file upload - for both box and setup images
  const handleImageUpload = async (e, imageType = 'box') => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await fetch('/api/upload', {
        method: 'POST',
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        if (imageType === 'box') {
          setValue('imageUrl', data.url);
          setPreviewBoxImage(data.url);
        } else {
          setValue('setupImageUrl', data.url);
          setPreviewSetupImage(data.url);
        }
      } else {
        alert(t.common.uploadFailed);
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert(t.common.uploadFailed);
    }
  };

  // Download image from URL
  const downloadImageFromUrl = async (imageType = 'box') => {
    const url = imageType === 'box' ? watchedImageUrl : watchedSetupImageUrl;
    if (!url || url.startsWith('/')) return;
    
    setIsDownloading(true);
    try {
      const res = await fetch('/api/bgg/download-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl: url,
          gameName: watchedName || 'game'
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        if (imageType === 'box') {
          setValue('imageUrl', data.localPath);
          setPreviewBoxImage(data.localPath);
        } else {
          setValue('setupImageUrl', data.localPath);
          setPreviewSetupImage(data.localPath);
        }
      } else {
        alert(t.common.uploadFailed);
      }
    } catch (err) {
      console.error('Download error:', err);
      alert(t.common.uploadFailed);
    } finally {
      setIsDownloading(false);
    }
  };

  const onSubmit = (data) => {
    if (game?.id) {
      data.id = game.id;
    }
    onSave(data);
  };

  // Render image upload section for a specific type
  const renderImageUploadSection = (imageType, fieldName, currentUrl, setPreview, searchQuery) => {
    const isBox = imageType === 'box';
    const accentColor = isBox ? '#f59e0b' : '#10b981';
    const accentColorLight = isBox ? '#fbbf24' : '#34d399';

    return (
      <div className="image-tabs-container">
        {/* Tab Headers */}
        <div style={{ display: 'flex', marginBottom: '-2px', position: 'relative', zIndex: 1 }}>
          <button 
            type="button"
            onClick={() => setImageSource('url')}
            style={{
              padding: '0.5rem 1rem',
              background: imageSource === 'url' ? '#1a1f2e' : 'transparent',
              color: imageSource === 'url' ? accentColor : '#8b949e',
              border: imageSource === 'url' ? `2px solid ${accentColor}` : '2px solid transparent',
              borderBottom: imageSource === 'url' ? '2px solid #1a1f2e' : `2px solid ${accentColor}`,
              borderRadius: '6px 6px 0 0',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: imageSource === 'url' ? '600' : '400',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              transition: 'all 0.2s ease',
              marginRight: '-2px'
            }}
          >
            <LinkIcon size={14} /> {t.common.url}
          </button>
          <button 
            type="button"
            onClick={() => setImageSource('upload')}
            style={{
              padding: '0.5rem 1rem',
              background: imageSource === 'upload' ? '#1a1f2e' : 'transparent',
              color: imageSource === 'upload' ? accentColor : '#8b949e',
              border: imageSource === 'upload' ? `2px solid ${accentColor}` : '2px solid transparent',
              borderBottom: imageSource === 'upload' ? '2px solid #1a1f2e' : `2px solid ${accentColor}`,
              borderRadius: '6px 6px 0 0',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: imageSource === 'upload' ? '600' : '400',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              transition: 'all 0.2s ease'
            }}
          >
            <Upload size={14} /> {t.common.upload}
          </button>
        </div>

        {/* Tab Content */}
        <div style={{
          border: `2px solid ${accentColor}`,
          borderRadius: '0 6px 6px 6px',
          padding: '0.75rem',
          background: '#1a1f2e'
        }}>
          {imageSource === 'url' && (
            <>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input 
                  {...register(fieldName)} 
                  placeholder={t.gameForm.imageUrlPlaceholder}
                  style={{ flex: 1, fontSize: '0.85rem', padding: '0.5rem' }}
                />
                {currentUrl && !currentUrl.startsWith('/') && (
                  <button
                    type="button"
                    onClick={() => downloadImageFromUrl(imageType)}
                    disabled={isDownloading}
                    style={{
                      background: accentColor,
                      color: 'white',
                      border: 'none',
                      padding: '0 0.75rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    title={t.gameForm.saveImageToServer}
                  >
                    {isDownloading ? <Loader size={14} /> : <Download size={14} />}
                  </button>
                )}
              </div>
            </>
          )}

          {imageSource === 'upload' && (
            <>
              {/* Google Images Search Helper */}
              {watchedName && (
                <div style={{
                  background: `${accentColor}15`,
                  border: `1px solid ${accentColor}40`,
                  borderRadius: '4px',
                  padding: '0.5rem',
                  marginBottom: '0.5rem',
                  fontSize: '0.8rem'
                }}>
                  <a 
                    href={`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(watchedName + ' ' + searchQuery)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      background: accentColor,
                      color: 'white',
                      padding: '0.4rem 0.75rem',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      fontSize: '0.8rem'
                    }}
                  >
                    <Search size={12} />
                    {formatMemberText(t.gameForm.searchFor, { query: `${watchedName} ${searchQuery}` })}
                  </a>
                </div>
              )}
              
              {/* File Upload Input */}
              <div style={{
                border: '2px dashed #4a5568',
                borderRadius: '4px',
                padding: '1rem',
                textAlign: 'center',
                cursor: 'pointer',
                background: `${accentColor}08`
              }}>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={(e) => handleImageUpload(e, imageType)}
                  style={{ display: 'none' }}
                  id={`game-${imageType}-image-upload`}
                />
                <label 
                  htmlFor={`game-${imageType}-image-upload`}
                  style={{ 
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                >
                  <Upload size={24} style={{ color: accentColorLight }} />
                  <span style={{ color: '#c9d1d9', fontSize: '0.8rem' }}>
                    {t.gameForm.clickToUpload}
                  </span>
                </label>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="card game-form">
      <h3>{game ? t.gameForm.editTitle : t.gameForm.addTitle}</h3>
      
      {/* AI Found Toast */}
      {aiFound && (
        <div className="ai-found-toast" style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '0.75rem 1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <Sparkles size={20} />
          <span><strong>{t.gameForm.aiFound}</strong> {t.gameForm.aiFoundDesc}</span>
        </div>
      )}

      {/* AI Game Info Card */}
      {aiGameInfo && !game && (
        <div style={{
          background: 'rgba(102, 126, 234, 0.1)',
          border: '1px solid rgba(102, 126, 234, 0.3)',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1rem',
          fontSize: '0.85rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <Brain size={16} style={{ color: '#667eea' }} />
            <strong style={{ color: '#667eea' }}>{t.gameForm.geminiInfo}</strong>
          </div>
          {aiGameInfo.description && (
            <p style={{ margin: '0.5rem 0', color: '#aaa' }}>{aiGameInfo.description}</p>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginTop: '0.5rem', color: '#888' }}>
            {aiGameInfo.playerCount && <span>👥 {aiGameInfo.playerCount}</span>}
            {aiGameInfo.playTime && <span>⏱️ {aiGameInfo.playTime}</span>}
            {aiGameInfo.complexity && <span>🎯 {aiGameInfo.complexity}</span>}
            {aiGameInfo.year && <span>📅 {aiGameInfo.year}</span>}
            {aiGameInfo.designer && <span>✏️ {aiGameInfo.designer}</span>}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-group">
          <label>{t.gameForm.nameLabel}</label>
          <div style={{ position: 'relative' }}>
            <input 
              {...register("name", { required: true })} 
              placeholder={t.gameForm.namePlaceholder}
              style={{ paddingRight: '3rem' }}
            />
            {isAiSearching && (
              <div style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}>
                <div className="ai-spinner" style={{
                  width: '20px',
                  height: '20px',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'pulse 1s ease-in-out infinite'
                }}>
                  <Brain size={12} color="white" />
                </div>
              </div>
            )}
          </div>
          {errors.name && <span className="error">{t.common.required}</span>}
          {!game && (
            <small style={{ color: '#888' }}>
              {t.gameForm.aiHint}
            </small>
          )}
        </div>

        <div className="form-group">
          <label>{t.gameForm.bggIdLabel}</label>
          <input {...register("bggId")} placeholder={t.gameForm.bggIdPlaceholder} />
          <small>{t.gameForm.bggIdHint}</small>
        </div>

        {/* Image Carousel Preview */}
        {allImages.length > 0 && (
          <div style={{
            background: '#161b22',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem',
            position: 'relative'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              gap: '1rem',
              position: 'relative'
            }}>
              {allImages.length > 1 && (
                <button
                  type="button"
                  onClick={() => setCarouselIndex((carouselIndex - 1 + allImages.length) % allImages.length)}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'white'
                  }}
                >
                  <ChevronLeft size={20} />
                </button>
              )}
              
              <div style={{ textAlign: 'center' }}>
                <img 
                  src={allImages[carouselIndex]?.url} 
                  alt={allImages[carouselIndex]?.label}
                  style={{ 
                    maxWidth: '180px', 
                    maxHeight: '180px', 
                    borderRadius: '8px',
                    border: `3px solid ${allImages[carouselIndex]?.type === 'box' ? '#f59e0b' : '#10b981'}`
                  }}
                  onError={(e) => { e.target.src = NO_IMAGE_URL; }}
                />
                <div style={{ 
                  marginTop: '0.5rem', 
                  color: allImages[carouselIndex]?.type === 'box' ? '#f59e0b' : '#10b981',
                  fontSize: '0.85rem',
                  fontWeight: '600'
                }}>
                  {allImages[carouselIndex]?.type === 'box' ? <Box size={14} style={{display: 'inline', marginRight: '4px'}} /> : <Layout size={14} style={{display: 'inline', marginRight: '4px'}} />}
                  {allImages[carouselIndex]?.label}
                </div>
              </div>

              {allImages.length > 1 && (
                <button
                  type="button"
                  onClick={() => setCarouselIndex((carouselIndex + 1) % allImages.length)}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'white'
                  }}
                >
                  <ChevronRight size={20} />
                </button>
              )}
            </div>

            {/* Carousel dots */}
            {allImages.length > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '0.75rem' }}>
                {allImages.map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setCarouselIndex(i)}
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      border: 'none',
                      background: i === carouselIndex ? (img.type === 'box' ? '#f59e0b' : '#10b981') : '#4a5568',
                      cursor: 'pointer'
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Image Type Tabs */}
        <div className="form-group">
          <label><ImageIcon size={16} /> {t.gameForm.imagesLabel}</label>
          
          {/* Main image type selector */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <button 
              type="button"
              onClick={() => setActiveImageTab('box')}
              style={{
                flex: 1,
                padding: '0.6rem',
                background: activeImageTab === 'box' ? '#f59e0b' : '#2d3748',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                fontWeight: activeImageTab === 'box' ? '600' : '400'
              }}
            >
              <Box size={16} /> {t.gameForm.boxImage}
              {previewBoxImage && <span style={{ 
                background: '#22c55e', 
                borderRadius: '50%', 
                width: '8px', 
                height: '8px',
                marginLeft: '4px'
              }} />}
            </button>
            <button 
              type="button"
              onClick={() => setActiveImageTab('setup')}
              style={{
                flex: 1,
                padding: '0.6rem',
                background: activeImageTab === 'setup' ? '#10b981' : '#2d3748',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                fontWeight: activeImageTab === 'setup' ? '600' : '400'
              }}
            >
              <Layout size={16} /> {t.gameForm.setupImage}
              {previewSetupImage && <span style={{ 
                background: '#22c55e', 
                borderRadius: '50%', 
                width: '8px', 
                height: '8px',
                marginLeft: '4px'
              }} />}
            </button>
          </div>

          {/* Description */}
          <small style={{ color: '#8b949e', marginBottom: '0.75rem', display: 'block' }}>
            {activeImageTab === 'box' 
              ? t.gameForm.boxImageHint
              : t.gameForm.setupImageHint}
          </small>

          {/* Upload section for selected type */}
          {activeImageTab === 'box' && renderImageUploadSection(
            'box', 
            'imageUrl', 
            watchedImageUrl, 
            setPreviewBoxImage,
            'board game box'
          )}
          
          {activeImageTab === 'setup' && renderImageUploadSection(
            'setup', 
            'setupImageUrl', 
            watchedSetupImageUrl, 
            setPreviewSetupImage,
            'board game setup'
          )}

          {/* Clear button for current image */}
          {((activeImageTab === 'box' && previewBoxImage) || (activeImageTab === 'setup' && previewSetupImage)) && (
            <button
              type="button"
              onClick={() => {
                if (activeImageTab === 'box') {
                  setValue('imageUrl', '');
                  setPreviewBoxImage('');
                } else {
                  setValue('setupImageUrl', '');
                  setPreviewSetupImage('');
                }
              }}
              style={{
                marginTop: '0.5rem',
                background: '#dc2626',
                color: 'white',
                border: 'none',
                padding: '0.4rem 0.75rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.8rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem'
              }}
            >
              <X size={14} /> {activeImageTab === 'box' ? t.gameForm.removeBoxImage : t.gameForm.removeSetupImage}
            </button>
          )}
        </div>

        <div className="form-group">
          <label><LinkIcon size={16} /> {t.gameForm.bggLinkLabel}</label>
          <input {...register("rulesUrl")} placeholder={t.gameForm.bggLinkPlaceholder} />
          <small>{t.gameForm.bggLinkHint}</small>
        </div>

        <div className="form-group">
          <label><FileText size={16} /> {t.gameForm.rulesPdfLabel}</label>
          <input {...register("manualUrl")} placeholder={t.gameForm.rulesPdfPlaceholder} />
          <small>{t.gameForm.rulesPdfHint}</small>
        </div>

        <div className="form-group">
          <label><Youtube size={16} /> {t.gameForm.videoLabel}</label>
          <input {...register("videoUrl")} placeholder={t.gameForm.videoPlaceholder} />
          <small>{t.gameForm.videoHint}</small>
        </div>

        {/* Expansions Section - Only for existing games */}
        {game?.id && (
          <div className="form-group" style={{ marginTop: '1.5rem' }}>
            <button
              type="button"
              onClick={() => setShowExpansions(!showExpansions)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.75rem 1rem',
                background: '#2d3748',
                border: '1px solid #4a5568',
                borderRadius: '8px',
                color: '#e2e8f0',
                cursor: 'pointer',
                fontSize: '0.95rem'
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Package size={18} />
                {formatMemberText(t.gameForm.expansions, { count: expansions.length })}
              </span>
              {showExpansions ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            
            {showExpansions && (
              <div style={{
                marginTop: '0.75rem',
                border: '1px solid #4a5568',
                borderRadius: '8px',
                padding: '1rem',
                background: '#1a1f2e'
              }}>
                {/* Existing Expansions List */}
                {loadingExpansions ? (
                  <div style={{ textAlign: 'center', padding: '1rem', color: '#888' }}>
                    <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                  </div>
                ) : expansions.length > 0 ? (
                  <div style={{ marginBottom: '1rem' }}>
                    {expansions.map(exp => (
                      <div 
                        key={exp.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.5rem',
                          background: '#2d3748',
                          borderRadius: '6px',
                          marginBottom: '0.5rem'
                        }}
                      >
                        {editingExpansion?.id === exp.id ? (
                          // Edit mode
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <input
                              value={editingExpansion.name}
                              onChange={(e) => setEditingExpansion({ ...editingExpansion, name: e.target.value })}
                              placeholder={t.gameForm.expansionName}
                              style={{ fontSize: '0.85rem', padding: '0.4rem' }}
                            />
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <input
                                value={editingExpansion.bggId || ''}
                                onChange={(e) => setEditingExpansion({ ...editingExpansion, bggId: e.target.value })}
                                placeholder="BGG ID"
                                style={{ width: '80px', fontSize: '0.8rem', padding: '0.3rem' }}
                              />
                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', color: '#aaa' }}>
                                <input
                                  type="checkbox"
                                  checked={editingExpansion.isOwned !== false}
                                  onChange={(e) => setEditingExpansion({ ...editingExpansion, isOwned: e.target.checked })}
                                />
                                {t.gameForm.owned}
                              </label>
                              <button
                                type="button"
                                onClick={() => handleUpdateExpansion(editingExpansion)}
                                style={{
                                  background: '#22c55e',
                                  color: 'white',
                                  border: 'none',
                                  padding: '0.3rem 0.6rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  cursor: 'pointer'
                                }}
                              >
                                {t.common.save}
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingExpansion(null)}
                                style={{
                                  background: '#64748b',
                                  color: 'white',
                                  border: 'none',
                                  padding: '0.3rem 0.6rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  cursor: 'pointer'
                                }}
                              >
                                {t.common.cancel}
                              </button>
                            </div>
                          </div>
                        ) : (
                          // View mode
                          <>
                            <div style={{ 
                              flex: 1, 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '0.5rem'
                            }}>
                              <span style={{ 
                                width: '10px', 
                                height: '10px', 
                                borderRadius: '50%', 
                                background: exp.is_owned ? '#22c55e' : '#64748b' 
                              }} title={exp.is_owned ? t.gameForm.owned : t.gameForm.notOwned} />
                              <span style={{ color: '#e2e8f0', fontSize: '0.9rem' }}>{exp.name}</span>
                              {exp.bgg_id && (
                                <a 
                                  href={`https://boardgamegeek.com/boardgameexpansion/${exp.bgg_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#3b82f6', fontSize: '0.75rem' }}
                                >
                                  BGG
                                </a>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => setEditingExpansion({ ...exp, isOwned: exp.is_owned, bggId: exp.bgg_id })}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#3b82f6',
                                cursor: 'pointer',
                                padding: '0.25rem'
                              }}
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteExpansion(exp.id)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#ef4444',
                                cursor: 'pointer',
                                padding: '0.25rem'
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                ) : null}

                {/* Available Expansions (Ghost List) */}
                {loadingAvailable ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '1rem', 
                    color: '#888',
                    borderTop: expansions.length > 0 ? '1px solid #4a5568' : 'none',
                    marginTop: expansions.length > 0 ? '0.75rem' : '0'
                  }}>
                    <Loader size={16} style={{ animation: 'spin 1s linear infinite', marginRight: '0.5rem' }} />
                    {t.gameForm.fetchingExpansions}
                  </div>
                ) : availableExpansions.length > 0 ? (
                  <div style={{
                    borderTop: expansions.length > 0 ? '1px solid #4a5568' : 'none',
                    paddingTop: expansions.length > 0 ? '0.75rem' : '0',
                    marginTop: expansions.length > 0 ? '0.75rem' : '0'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem', 
                      marginBottom: '0.5rem',
                      color: '#888',
                      fontSize: '0.8rem'
                    }}>
                      <Sparkles size={14} style={{ color: '#a855f7' }} />
                      <span>
                        {formatMemberText(t.gameForm.foundExpansions, { source: expansionSource === 'bgg' ? 'BGG' : 'AI' })}
                      </span>
                    </div>
                    {availableExpansions.map((exp, idx) => (
                      <div 
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.5rem',
                          background: 'rgba(168, 85, 247, 0.1)',
                          border: '1px dashed rgba(168, 85, 247, 0.3)',
                          borderRadius: '6px',
                          marginBottom: '0.5rem'
                        }}
                      >
                        <div style={{ 
                          flex: 1, 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.5rem',
                          opacity: 0.8
                        }}>
                          <span style={{ 
                            width: '10px', 
                            height: '10px', 
                            borderRadius: '50%', 
                            border: '2px dashed #a855f7',
                            background: 'transparent'
                          }} />
                          <span style={{ color: '#c9d1d9', fontSize: '0.9rem' }}>{exp.name}</span>
                          {exp.bggId && (
                            <a 
                              href={`https://boardgamegeek.com/boardgameexpansion/${exp.bggId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#a855f7', fontSize: '0.75rem' }}
                            >
                              BGG
                            </a>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleClaimExpansion(exp)}
                          style={{
                            background: '#22c55e',
                            color: 'white',
                            border: 'none',
                            padding: '0.35rem 0.75rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3rem'
                          }}
                        >
                          <Plus size={12} /> {t.gameForm.iOwn}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : expansions.length === 0 && !loadingAvailable ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '1rem', 
                    color: '#64748b',
                    fontSize: '0.85rem'
                  }}>
                    {t.gameForm.noExpansionsFound}
                  </div>
                ) : null}

                {/* Add New Expansion Form */}
                <div style={{
                  borderTop: expansions.length > 0 ? '1px solid #4a5568' : 'none',
                  paddingTop: expansions.length > 0 ? '0.75rem' : '0'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    gap: '0.5rem', 
                    flexWrap: 'wrap',
                    alignItems: 'flex-end'
                  }}>
                    <div style={{ flex: '1 1 150px' }}>
                      <label style={{ fontSize: '0.75rem', color: '#888', display: 'block', marginBottom: '0.25rem' }}>
                        {t.gameForm.expansionNameRequired}
                      </label>
                      <input
                        value={newExpansion.name}
                        onChange={(e) => setNewExpansion({ ...newExpansion, name: e.target.value })}
                        placeholder={t.gameForm.expansionNamePlaceholder}
                        style={{ width: '100%', fontSize: '0.85rem', padding: '0.4rem' }}
                      />
                    </div>
                    <div style={{ width: '80px' }}>
                      <label style={{ fontSize: '0.75rem', color: '#888', display: 'block', marginBottom: '0.25rem' }}>
                        BGG ID
                      </label>
                      <input
                        value={newExpansion.bggId}
                        onChange={(e) => setNewExpansion({ ...newExpansion, bggId: e.target.value })}
                        placeholder="ID"
                        style={{ width: '100%', fontSize: '0.85rem', padding: '0.4rem' }}
                      />
                    </div>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.3rem', 
                      fontSize: '0.8rem', 
                      color: '#aaa',
                      paddingBottom: '0.4rem'
                    }}>
                      <input
                        type="checkbox"
                        checked={newExpansion.isOwned}
                        onChange={(e) => setNewExpansion({ ...newExpansion, isOwned: e.target.checked })}
                      />
                      {t.gameForm.owned}
                    </label>
                    <button
                      type="button"
                      onClick={handleAddExpansion}
                      disabled={!newExpansion.name.trim()}
                      style={{
                        background: newExpansion.name.trim() ? '#22c55e' : '#4a5568',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 0.75rem',
                        borderRadius: '6px',
                        cursor: newExpansion.name.trim() ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.3rem',
                        fontSize: '0.85rem'
                      }}
                    >
                      <Plus size={16} /> {t.common.add}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button type="button" className="btn-secondary" onClick={onCancel}>{t.common.cancel}</button>
          <button type="submit" className="btn-primary"><Save size={18} /> {t.common.save}</button>
        </div>
      </form>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
        @keyframes slideIn {
          from { transform: translateY(-10px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default GameForm;
